import { useCallback, useState } from 'react';
import { Moment } from 'moment';

import {
  useAddMailLabelMutation,
  useApplyMailThreadsLabelMutation,
  useCancelScheduledMailMutation,
  useEditMailLabelMutation,
  useMarkMailMessagesReadMutation,
  useRemoveMailLabelMutation,
  useRequestMailSyncMutation,
  useRunMailThreadsActionMutation,
  useSnoozeMailThreadsMutation,
  useUnsnoozeMailThreadsMutation,
} from 'core/graphql';

import {
  IMailResult,
  MailActionKey,
  mailActionEnum,
  mailFailure,
} from './mailModel';
import { IMailLabel } from './useMailboxes';

type Envelope = IMailResult | null | undefined;

const unwrap = (result: Envelope): IMailResult =>
  result
    ? { ok: result.ok, error: result.error, errorCode: result.errorCode }
    : { ok: false, errorCode: 'UNAVAILABLE' };

/**
 * ТЗ 0122 — действия над тредами, метками и ящиками. Всё уходит в Gmail
 * (сервер меняет индекс только при успехе Gmail), ответ —
 * `{ ok, error, errorCode }`. Перечитывать список и тред — вызывающему
 * (`reload` списка курсором, без `refetchQueries` по имени).
 */
export const useMailActions = () => {
  const [actionMut] = useRunMailThreadsActionMutation();
  const [labelMut] = useApplyMailThreadsLabelMutation();
  const [snoozeMut] = useSnoozeMailThreadsMutation();
  const [unsnoozeMut] = useUnsnoozeMailThreadsMutation();
  const [readMut] = useMarkMailMessagesReadMutation();
  const [createLabelMut] = useAddMailLabelMutation();
  const [updateLabelMut] = useEditMailLabelMutation();
  const [deleteLabelMut] = useRemoveMailLabelMutation();
  const [syncMut] = useRequestMailSyncMutation();
  const [cancelMut] = useCancelScheduledMailMutation();
  const [busy, setBusy] = useState(false);

  const guard = useCallback(
    async (run: () => Promise<Envelope>): Promise<IMailResult> => {
      setBusy(true);
      try {
        return unwrap(await run());
      } catch (caught) {
        return mailFailure(caught);
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const threadsAction = useCallback(
    (threadIds: string[], action: MailActionKey) =>
      guard(async () => {
        const { data } = await actionMut({
          variables: { threadIds, action: mailActionEnum(action) },
        });
        return data?.mailThreadsAction;
      }),
    [guard, actionMut],
  );

  const label = useCallback(
    (threadIds: string[], add: string[], remove: string[]) =>
      guard(async () => {
        const { data } = await labelMut({
          variables: { threadIds, addLabelIds: add, removeLabelIds: remove },
        });
        return data?.mailThreadsLabel;
      }),
    [guard, labelMut],
  );

  const snooze = useCallback(
    (threadIds: string[], until: Moment) =>
      guard(async () => {
        const { data } = await snoozeMut({
          variables: { threadIds, until: until.toISOString() },
        });
        return data?.mailSnooze;
      }),
    [guard, snoozeMut],
  );

  const unsnooze = useCallback(
    (threadIds: string[]) =>
      guard(async () => {
        const { data } = await unsnoozeMut({ variables: { threadIds } });
        return data?.mailUnsnooze;
      }),
    [guard, unsnoozeMut],
  );

  const markMessagesRead = useCallback(
    async (messageIds: string[], read: boolean): Promise<IMailResult> => {
      if (messageIds.length === 0) return { ok: true };
      try {
        const { data } = await readMut({ variables: { messageIds, read } });
        return unwrap(data?.mailMarkMessagesRead);
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [readMut],
  );

  const createLabel = useCallback(
    async (
      mailboxId: string,
      name: string,
      colorBg: string | null,
    ): Promise<IMailResult & { label?: IMailLabel | null }> => {
      try {
        const { data } = await createLabelMut({
          variables: { mailboxId, name, colorBg },
        });
        const result = data?.mailCreateLabel;
        return { ...unwrap(result), label: result?.label };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [createLabelMut],
  );

  const updateLabel = useCallback(
    (labelId: string, name: string | null, colorBg: string | null) =>
      guard(async () => {
        const { data } = await updateLabelMut({
          variables: { labelId, name, colorBg },
        });
        return data?.mailUpdateLabel;
      }),
    [guard, updateLabelMut],
  );

  const deleteLabel = useCallback(
    (labelId: string) =>
      guard(async () => {
        const { data } = await deleteLabelMut({ variables: { labelId } });
        return data?.mailDeleteLabel;
      }),
    [guard, deleteLabelMut],
  );

  const syncNow = useCallback(
    (mailboxId: string | null) =>
      guard(async () => {
        const { data } = await syncMut({ variables: { mailboxId } });
        return data?.mailSyncNow;
      }),
    [guard, syncMut],
  );

  const cancelScheduled = useCallback(
    (id: string) =>
      guard(async () => {
        const { data } = await cancelMut({ variables: { id } });
        return data?.mailCancelScheduled;
      }),
    [guard, cancelMut],
  );

  return {
    busy,
    threadsAction,
    label,
    snooze,
    unsnooze,
    markMessagesRead,
    createLabel,
    updateLabel,
    deleteLabel,
    syncNow,
    cancelScheduled,
  };
};

export type IMailActions = ReturnType<typeof useMailActions>;
